import React, { useContext } from 'react'
import OfferUrgencyLabels from 'components/Common/Labels/OfferUrgencyLabels'
import NumberRating from 'components/Common/NumberRating'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import SearchTileCancellationPolicy from 'components/SearchV2/SearchTileCancellationPolicy/SearchTileCancellationPolicy'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { showUserReviewsRating } from 'lib/reviewUtils'
import OfferTileLocationSection from '../OfferTileLocationSection'
import OfferTilePropertyHeading from '../OfferTilePropertyHeading'
import TileInclusions from '../../Support/OfferTileInclusions'
import styled from 'styled-components'
import shouldHideAoLabel from 'lib/hideAoLabel'
import { rem } from 'polished'
import { connect } from 'react-redux'
import { getShowOnMapOfferExperimentVariant } from 'selectors/searchSelectors'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { encodeOfferIds } from 'components/Pages/HotelSearchPage/HotelSearchMapPage'
import { queryKeySelectedOfferIds } from 'constants/url'
import { setSearchParamValue } from 'lib/urlUtils'
import {
  OptimizelyClientSideExperiments,
  useOptimizelyExperiment,
} from 'lib/optimizely'
import useSearchTileInclusionUpsellBestPrice from 'hooks/useSearchTileInclusionUpsellBestPrice'
import { useLPCTacticalSearchTilePromotions } from 'hooks/OfferPage/useLPCTacticalPromotions'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL, OFFER_TYPE_LAST_MINUTE } from 'constants/offer'
import { useIsMobileAndSmallScreen } from 'lib/deviceUtils'
import { useTranslation } from 'react-i18next'
import { isLPCTacticalEnabled } from 'lib/offerUtils'
import { convertMarkdownInclusionsToArray } from 'lib/markdownUtil'

const PropertyDetails = styled.div`
  > * + * {
    margin-top: ${rem(4)};
  }
`

const HeaderActions = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

interface MappedStateProps {
  shouldShowMapButton: boolean;
  windowSearch: string;
}

interface Props {
  location: string;
  cancellationPolicyType: string;
  offer: App.OfferSummary;
  checkInDate: moment.Moment | undefined;
  filters: App.OfferListFilters | undefined;
  offerUrl: string
}

function SearchTilePropertyDetails(props: Props & MappedStateProps) {
  const {
    shouldShowMapButton,
    location,
    cancellationPolicyType,
    offer,
    checkInDate,
    windowSearch,
    offerUrl,
    filters,
  } = props

  const {
    property,
    name,
    tileInclusions,
    tileInclusionsHeading,
    tileDescription,
    type,
    productType,
    id,
  } = offer

  const isMobile = useIsMobileAndSmallScreen()
  const { t } = useTranslation('common')

  const showMoreInclusions = !!useOptimizelyExperiment(
    OptimizelyClientSideExperiments.searchShowMoreInclusions,
  ) && !isMobile

  const { searchTargetLabel, offerDistanceFromSearchTarget, searchType } =
    useContext(GlobalSearchStateContext)
  const searchTargetDistance = offerDistanceFromSearchTarget?.[id]
  const showSearchDistance =
    searchType === 'landmark' &&
    !!searchTargetLabel &&
    typeof searchTargetDistance === 'number'

  const hideAoLabel = shouldHideAoLabel(offer)

  let inclusionsHeading: string

  if (type === OFFER_TYPE_LAST_MINUTE) {
    inclusionsHeading = tileInclusionsHeading ?? 'Exclusive extras:'
  } else {
    inclusionsHeading = tileInclusionsHeading ?? 'Handpicked inclusions:'
  }

  const { shouldShowUpsell: showInclusionUpsell } =
    useSearchTileInclusionUpsellBestPrice(offer, filters)

  const meetTactical = isLPCTacticalEnabled() && type === OFFER_TYPE_ALWAYS_ON && (offer?.hasTactical ?? false)
  const { bestPrice: LPCTacticalUpsellPackage, shouldShowUpsell: shouldShowLPCTacticalUpsell } = useLPCTacticalSearchTilePromotions(offer, filters?.checkIn, filters?.checkOut)
  const showLPCTacticalLabel = meetTactical && !shouldShowLPCTacticalUpsell

  const bonusInclusions: App.TileInclusion[] = []
  if (showLPCTacticalLabel) {
    LPCTacticalUpsellPackage?.inclusions?.filter(inclusion => inclusion.hasTactical)
      .map(bonusInclusion => {
        if ((bonusInclusion?.descriptionV2List?.length ?? 0) > 0) {
          return bonusInclusion.descriptionV2List?.map((inclusionV2) => {
            bonusInclusions.push({
              text: inclusionV2.text,
              isHighlighted: inclusionV2.isHighlighted,
              icon: inclusionV2?.icon,
            })
          })
        } else {
          const description = bonusInclusion?.description?.startsWith('-') ?
            bonusInclusion?.description?.slice(1).trim() :
            bonusInclusion.description

          const descriptionLines = description ? convertMarkdownInclusionsToArray(description) : []
          descriptionLines.map((line) => {
            bonusInclusions.push({
              text: line,
              isHighlighted: false,
              icon: bonusInclusion?.categoryIcon || '',
            })
          })
        }
      })
    inclusionsHeading = 'Bonus inclusions for select packages:'
  }

  const showBonusInclusions = showLPCTacticalLabel && bonusInclusions.length > 0

  const shouldShowInclusions =
  type === OFFER_TYPE_HOTEL ||
    (!!tileInclusions &&
    tileInclusions.length > 0 &&
    !showInclusionUpsell)

  // if we're showing more inclusions or if the number of inclusions is more than 3, don't show the hotel description
  const hotelDescription =
    !showMoreInclusions ||
    tileInclusions?.length === undefined ||
    tileInclusions?.length < 3 ?
      name :
      undefined

  const mapSearchUrl = `/search/map?${setSearchParamValue(
    windowSearch,
    queryKeySelectedOfferIds,
    encodeOfferIds([offer]),
  )}`

  return (
    <PropertyDetails>
      <Group direction="vertical" fullWidth gap={4}>
        {property && showUserReviewsRating(property.rating, property.reviewsTotal) && (
          <CSSBreakpoint min="md">
            <HeaderActions>
              <NumberRating
                variant="medium"
                rating={property.rating}
                total={property.reviewsTotal}
                ratingSubtitle={property.reviewsSource === 'google' ? t('From online reviews', { ns: 'common' }) : undefined}
              />
            </HeaderActions>
          </CSSBreakpoint>
        )}
        <OfferUrgencyLabels offer={offer} />
        <OfferTileLocationSection
          location={location}
          showSearchDistance={showSearchDistance}
          shouldShowMapLink={shouldShowMapButton}
          mapSearchUrl={mapSearchUrl}
          searchTargetDistance={searchTargetDistance}
          searchTargetLabel={searchTargetLabel}
        />
      </Group>
      <div>
        {property && (
          <OfferTilePropertyHeading
            name={property.name}
            descriptiveName={hotelDescription}
          />
        )}
      </div>

      {property && showUserReviewsRating(property.rating, property.reviewsTotal) && (
        <CSSBreakpoint max="sm">
          <NumberRating
            inlineLabel
            variant="xs"
            rating={property.rating}
            total={property.reviewsTotal}
            ratingSubtitle={property.reviewsSource === 'google' ? t('From online reviews', { ns: 'common' }) : undefined}
          />
        </CSSBreakpoint>
      )}
      {showLPCTacticalLabel ? <ProductTypeLabel productType="limited_time_special" kind="plain" /> : (!hideAoLabel && (<ProductTypeLabel productType={productType} kind="plain" />))}
      <SearchTileCancellationPolicy
        cancellationPolicyType={cancellationPolicyType}
        checkInDate={checkInDate}
        timezoneOffset={offer?.property?.timezoneOffset}
      />
      {(tileInclusions?.length || tileDescription) &&
        shouldShowInclusions && (
          <TileInclusions
            offerTitle={offer.property?.name ? offer.property.name : offer.name}
            offerURL={offerUrl}
            content={tileDescription}
            tileInclusionsList={tileInclusions}
            heading={inclusionsHeading}
            maxInclusionsCount={showMoreInclusions ? 5 : 3}
          />
      )}
      {showBonusInclusions && (
        <TileInclusions
          offerTitle={offer.property?.name ? offer.property.name : offer.name}
          offerURL={offerUrl}
          tileInclusionsList={bonusInclusions}
          heading={inclusionsHeading}
          maxInclusionsCount={5}
        />
      )}
    </PropertyDetails>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>(
  (state): MappedStateProps => ({
    shouldShowMapButton: getShowOnMapOfferExperimentVariant(state),
    windowSearch: state.router.location.search,
  }),
)(SearchTilePropertyDetails)
